import React, { useEffect, useState, useContext } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import CreateIcon from '@material-ui/icons/Create';
import formatSerializedId from '../../utils/formatSerializedId';
import { i18n } from "../../translate/i18n";
import ModalImageCors from "../ModalImageCors"
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import { CardHeader, Switch, Tooltip } from "@material-ui/core";
import { ContactForm } from "../ContactForm";
import ContactModal from "../ContactModal";
import { ContactNotes } from "../ContactNotes";

import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from "react-toastify";
import { TagsKanbanContainer } from "../TagsKanbanContainer";
import { TagsAtendimentoContainer } from "../TagsAtendimentoContainer";


const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		border: "none",
		borderRadius: 16,
		height: "calc(100% - 32px)",
		margin: 16, // Or top/right/bottom/left if needed, but margin works with absolute in some flex contexts if configured, but let's stick to safe positioning if we can. 
		// Actually, with position: absolute (from JSX), margin might offset it or top/right is better. 
		// Let's rely on the fact that it's absolute.
		top: 0, // We will offset via margin or top.
		right: 0,
		boxShadow: theme.shadows[3],
	},
	header: {
		display: "flex",
		backgroundColor: theme.palette.inputBackground,
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "50px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		backgroundColor: theme.palette.inputBackground,
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		justifyContent: "flex-start",
		overflowY: "auto",
		overflowX: "hidden",
		...theme.scrollbarStyles,
		'&::-webkit-scrollbar': {
			width: '8px',
			height: '8px',
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: theme.mode === 'light' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
			borderRadius: '8px',
		},
		'&::-webkit-scrollbar-track': {
			backgroundColor: 'transparent',
		},
	},

	contactAvatar: {
		margin: 15,
		width: 160,
		height: 160,
		borderRadius: 10,
	},

	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		"& > *": {
			margin: 4,
		},
	},

	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const [modalOpen, setModalOpen] = useState(false);
	const [blockingContact, setBlockingContact] = useState(contact.active);
	const [openForm, setOpenForm] = useState(false);
	const { get } = useCompanySettings();
	const [hideNum, setHideNum] = useState(false);
	const { user } = useContext(AuthContext);
	const [acceptAudioMessage, setAcceptAudio] = useState(contact.acceptAudioMessage);

	useEffect(() => {
		async function fetchData() {

			const lgpdHideNumber = await get({
				"column": "lgpdHideNumber"
			});

			if (lgpdHideNumber === "enabled") setHideNum(true);

		}
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		setOpenForm(false);
	}, [open, contact]);



	const handleContactToggleAcceptAudio = async () => {
		try {
			const contact = await api.put(`/contacts/toggleAcceptAudio/${ticket.contact.id}`);
			setAcceptAudio(contact.data.acceptAudioMessage);
		} catch (err) {
			toastError(err);
		}
	};

	const handleBlockContact = async (contactId) => {
		try {
			await api.put(`/contacts/block/${contactId}`, { active: false });
			toast.success("Contato bloqueado");
		} catch (err) {
			toastError(err);
		}

		setBlockingContact(true);
	};

	const handleUnBlockContact = async (contactId) => {
		try {
			await api.put(`/contacts/block/${contactId}`, { active: true });
			toast.success("Contato desbloqueado");
		} catch (err) {
			toastError(err);
		}
		setBlockingContact(false);
	};

	if (loading) return null;
	if (!open) return null;

	return (
		<Paper
			elevation={0}
			className={classes.drawerPaper} // We will reuse/tweak this class
			style={{
				width: isMobile ? "100%" : drawerWidth, // 320px
				height: "100%", // Full height of parent flex container
				position: isMobile ? "absolute" : "relative",
				zIndex: isMobile ? 1200 : 1, // Cover everything on mobile
				top: isMobile ? 0 : 'auto',
				left: isMobile ? 0 : 'auto',
				display: "flex",
				flexDirection: "column",
				borderRadius: isMobile ? 0 : 16, // Rounded corners
				boxShadow: "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)", // shadow-1 equiv manually or use class
				border: "none",
				margin: 0, // No margin needed, Gap handles it
				flexShrink: 0, // Don't shrink
				overflow: "hidden", // Ensure children (header) don't overflow rounded corners
			}}
		>
			<div className={classes.header}>
				<IconButton onClick={handleDrawerClose}>
					<CloseIcon />
				</IconButton>
				<Typography style={{ justifySelf: "center" }}>
					{i18n.t("contactDrawer.header")}
				</Typography>
			</div>
			<div>
				{!loading ? (
					<>
						{/* <Typography
							style={{ marginBottom: 8, marginTop: 12 }}
							variant="subtitle1"
						>
							<Switch
								size="small"
								checked={acceptAudioMessage}
								onChange={() => handleContactToggleAcceptAudio()}
								name="disableBot"
								color="primary"
							/>
							{i18n.t("ticketOptionsMenu.acceptAudioMessage")}
						</Typography> */}
					</>
				) : (<br />)}
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					<Paper elevation={0} className={classes.contactHeader} style={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
						<Avatar
							src={contact?.urlPicture}
							style={{ width: 100, height: 100, marginBottom: 16, borderRadius: '50%' }}
						/>
						<Typography variant="h5" style={{ fontWeight: 600 }}>
							{contact.name}
						</Typography>
						<Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
							{hideNum && user.profile === "user" ? formatSerializedId(contact.number).slice(0, -6) + "**-**" + contact.number.slice(-2) : formatSerializedId(contact.number)}
						</Typography>

						{/* Messenger Actions Grid */}
						{/* Messenger Actions Grid - HIDDEN */}
						{/* <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginBottom: 24, gap: 24 }}>
							<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }} onClick={() => setModalOpen(!openForm)}>
								<div style={{
									width: 40, height: 40, borderRadius: '50%', backgroundColor: '#e4e6eb',
									display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6
								}}>
									<CreateIcon fontSize="small" style={{ color: '#000' }} />
								</div>
								<Typography variant="caption" style={{ color: '#000' }}>Perfil</Typography>
							</div>

							<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
								<div style={{
									width: 40, height: 40, borderRadius: '50%', backgroundColor: '#e4e6eb',
									display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6
								}}>
									<Switch
										size="small"
										checked={acceptAudioMessage}
										onChange={() => handleContactToggleAcceptAudio()}
										name="disableBot"
									/>
								</div>
								<Typography variant="caption" style={{ color: '#000' }}>Áudio</Typography>
							</div>

							<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
								<div style={{
									width: 40, height: 40, borderRadius: '50%', backgroundColor: '#e4e6eb',
									display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6
								}}>
									<CloseIcon fontSize="small" style={{ color: '#000' }} onClick={handleDrawerClose} />
								</div>
								<Typography variant="caption" style={{ color: '#000' }}>Fechar</Typography>
							</div>
						</div> */}

						{(contact.id && openForm) && <ContactForm initialContact={contact} onCancel={() => setOpenForm(false)} />}
					</Paper>

					{/* Collapsible-like Sections */}
					<div style={{ padding: '0 16px' }}>
						{/* <Typography variant="subtitle2" style={{ fontWeight: 600, padding: '12px 0', cursor: 'pointer' }}>
							Mídia, arquivos e links
						</Typography>

						<Typography variant="subtitle2" style={{ fontWeight: 600, padding: '12px 0', cursor: 'pointer' }}>
							Privacidade e suporte
						</Typography>
						<div style={{ paddingLeft: 12 }}>
							<Button
								fullWidth
								variant="text"
								color="secondary"
								onClick={() => contact.active ? handleBlockContact(contact.id) : handleUnBlockContact(contact.id)}
								style={{ justifyContent: 'flex-start', textTransform: 'none' }}
							>
								{!contact.active ? "Desbloquear contato" : "Bloquear contato"}
							</Button>
						</div> */}

						<Typography variant="subtitle2" style={{ fontWeight: 600, padding: '12px 0', marginTop: 16 }}>
							Dados do Sistema (Tags e Notas)
						</Typography>

						<TagsKanbanContainer ticket={ticket} className={classes.contactTags} />

						<div style={{ marginTop: 12, marginBottom: 4, borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: 12 }}>
							<TagsAtendimentoContainer ticket={ticket} />
						</div>

						<Paper square variant="outlined" className={classes.contactDetails} style={{ border: 'none', padding: 0 }}>
							<ContactNotes ticket={ticket} />
						</Paper>

						<Paper square variant="outlined" className={classes.contactDetails} style={{ border: 'none' }}>
							<ContactModal
								open={modalOpen}
								onClose={() => setModalOpen(false)}
								contactId={contact.id}
							></ContactModal>
							{/* <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
								{i18n.t("contactDrawer.extraInfo")}
							</Typography>
							{contact?.extraInfo?.map(info => (
								<Paper
									key={info.id}
									square
									variant="outlined"
									className={classes.contactExtraInfo}
									style={{ border: 'none', background: '#f5f5f5', borderRadius: 8, marginBottom: 4 }}
								>
									<InputLabel>{info.name}</InputLabel>
									<Typography component="div" noWrap style={{ paddingTop: 2 }}>
										<MarkdownWrapper>{info.value}</MarkdownWrapper>
									</Typography>
								</Paper>
							))} */}
						</Paper>
					</div>
				</div>
			)}
		</Paper>
	);
};

export default ContactDrawer;
